import { create } from 'zustand';
import { api } from '../api/client';
import { useUIStore } from './uiStore';

/**
 * Gustos del propio usuario, guardados en la base (no en el navegador) para que lo sigan de una
 * terminal a otra. NO son permisos: no deciden qué puede hacer, solo cómo se le muestra.
 */
export interface UserPreferences {
  /** Depósito: mostrar el chat con el comprador dentro del pop-up de cierre de orden. */
  wh_popup_messages?: boolean;
  [key: string]: unknown;
}

interface AuthUser {
  id: number;
  username: string;
  role: string;
  role_name?: string;
  tenant_id: number;
  display_name?: string;
  is_admin: boolean;
  capabilities: string[];
  seller_ids: number[] | null;
  must_change_password?: boolean;
  preferences?: UserPreferences;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  /** Tras cambiar la propia contraseña: saca la marca de "tiene que cambiarla". */
  markPasswordChanged: () => void;
  /**
   * Refleja en la sesión las preferencias que ya devolvió el servidor tras guardarlas.
   * NO guarda nada por su cuenta: quien llama ya hizo el PUT y pasa lo que vino de vuelta.
   */
  setPreferences: (preferences: UserPreferences) => void;
  /**
   * true si el usuario es admin o tiene la capacidad. Sin sesión → false.
   * Para condicionar render usar useCan() (este getter no re-renderiza).
   */
  can: (cap: string) => boolean;
}

/**
 * R21 — Quien NO puede ver todos los vendedores no tiene selector de vendedor en la barra de
 * arriba, pero cada pantalla igual manda `?seller_id=` con lo último guardado en el navegador (o
 * el 1 por defecto). Al entrar se lo acota a uno de los suyos para que vea SUS datos.
 * A los administradores y a quien tiene `global.all_sellers` no se les toca nada.
 */
export function acotarVendedor(user: AuthUser): void {
  const veTodos = user.is_admin || (user.capabilities ?? []).includes('global.all_sellers');
  if (veTodos) return;
  useUIStore.getState().ensureSellerScope(user.seller_ids);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (username: string, password: string) => {
    set({ error: null, isLoading: true });
    try {
      const res = await api.post<AuthUser>('/auth/login', { username, password });
      if (res.success && res.data) {
        const user = res.data as unknown as AuthUser;
        acotarVendedor(user);
        set({ user, isAuthenticated: true, isLoading: false });
        return true;
      }
      set({ error: res.error || 'Login failed', isLoading: false });
      return false;
    } catch {
      set({ error: 'Connection error', isLoading: false });
      return false;
    }
  },

  logout: async () => {
    await api.post('/auth/logout', {}).catch(() => {});
    set({ user: null, isAuthenticated: false });
  },

  checkAuth: async () => {
    try {
      const res = await api.get<AuthUser | null>('/auth/me');
      if (res.success && res.data) {
        const user = res.data as unknown as AuthUser;
        acotarVendedor(user);
        set({ user, isAuthenticated: true, isLoading: false });
      } else {
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  markPasswordChanged: () => {
    const u = get().user;
    if (!u) return;
    set({ user: { ...u, must_change_password: false } });
  },

  setPreferences: (preferences) => {
    const u = get().user;
    if (!u) return;
    set({ user: { ...u, preferences: preferences ?? {} } });
  },

  can: (cap) => {
    const u = get().user;
    if (!u) return false;
    return u.is_admin || (u.capabilities ?? []).includes(cap);
  },
}));
